<template>
    <div class="admin-panel">
        <div class="admin-header">
            <div class="admin-header-content">
                <div>
                    <h1>Администрирование</h1>
                    <p class="admin-subtitle">{{ subtitle }}</p>
                </div>
            </div>
        </div>

        <div class="admin-tabs" role="tablist">
            <template v-for="(tab, index) in tabs" :key="tab.id">
                <span
                    v-if="index > 0 && tab.group !== tabs[index - 1].group"
                    class="admin-tabs-separator"
                    aria-hidden="true"
                >
                    <span class="admin-tabs-separator-label">{{ ADMIN_TAB_GROUP_LABEL[tab.group] }}</span>
                </span>
                <button
                    type="button"
                    :class="['admin-tab', { active: activeTab === tab.id }]"
                    role="tab"
                    :aria-selected="activeTab === tab.id"
                    @click="setActiveTab(tab.id)"
                >
                    {{ tab.label }}
                </button>
            </template>
        </div>

        <div class="admin-content" role="tabpanel">
            <StructureTab
                v-if="activeTab === ADMIN_TAB.STRUCTURE"
                v-model:structure="structure"
                :actor="userData"
                :users="users"
                @open-users="openUsersTab"
            />

            <UsersTab
                v-else-if="activeTab === ADMIN_TAB.USERS"
                v-model:users="users"
                :actor="userData"
                :roles="data.roles || []"
                :partners="structure.partners"
                :advertisers="structure.advertisers"
                :initial-filters="usersInitialFilters"
            />

            <!-- Инструменты сохраняют состояние и загруженные данные при переключении вкладок. -->
            <KeepAlive>
                <Suspense v-if="toolComponent" :key="activeTab">
                    <component :is="toolComponent" :actor="userData" />
                    <template #fallback>
                        <p class="admin-content__loading" role="status">Загрузка…</p>
                    </template>
                </Suspense>
            </KeepAlive>
        </div>
    </div>
</template>

<script setup>
import { computed, defineAsyncComponent, ref } from 'vue';
import StructureTab from './StructureTab.vue';
import UsersTab from './UsersTab.vue';
import { ADMIN_TAB, ADMIN_TABS, ADMIN_TAB_GROUP_LABEL, DEFAULT_ADMIN_TAB, adminSubtitle } from '../../constants/admin';

/** Инструменты грузятся отдельными чанками: их код и данные нужны не каждому визиту. */
const TOOL_COMPONENTS = Object.freeze({
    [ADMIN_TAB.CALCULATOR]: defineAsyncComponent(() => import(/* webpackChunkName: "admin-calculator" */ '../calculator/CalculatorTab.vue')),
    [ADMIN_TAB.SCREEN_PACKAGES]: defineAsyncComponent(() => import(/* webpackChunkName: "admin-screen-packages" */ '../screen-packages/ScreenPackagesTab.vue')),
    [ADMIN_TAB.AIRTIME_CERTIFICATE]: defineAsyncComponent(() => import(/* webpackChunkName: "admin-airtime-certificate" */ './AirtimeCertificateTab.vue')),
    [ADMIN_TAB.ACTIVITY_LOG]: defineAsyncComponent(() => import(/* webpackChunkName: "admin-activity-log" */ './ActivityLogTab.vue')),
    [ADMIN_TAB.SCORING]: defineAsyncComponent(() => import(/* webpackChunkName: "admin-scoring" */ './ScoringGatewayTab.vue')),
});

const props = defineProps({
    data: { type: Object, required: true },
    userData: { type: Object, required: true },
});

const tabs = computed(() => ADMIN_TABS.filter(tab => tab.isVisibleTo(props.userData)));
const subtitle = computed(() => adminSubtitle(props.userData));

const users = ref([...(props.data.users || [])]);
const structure = ref({ ...props.data.structure });
const usersInitialFilters = ref({});

const isAvailableTab = tabId => tabs.value.some(tab => tab.id === tabId);

const tabFromUrl = new URLSearchParams(window.location.search).get('tab');
const activeTab = ref(isAvailableTab(tabFromUrl) ? tabFromUrl : DEFAULT_ADMIN_TAB);
const toolComponent = computed(() => TOOL_COMPONENTS[activeTab.value] || null);

const setActiveTab = (tabId, usersFilters = {}) => {
    usersInitialFilters.value = usersFilters;
    activeTab.value = tabId;

    const url = new URL(window.location.href);
    if (tabId === DEFAULT_ADMIN_TAB) url.searchParams.delete('tab');
    else url.searchParams.set('tab', tabId);
    window.history.replaceState({}, '', url.pathname + url.search);
};

const openUsersTab = partnerId => setActiveTab(ADMIN_TAB.USERS, partnerId ? { partnerId } : {});
</script>

<style lang="scss">
@use '../../../scss/pages/admin/admin-panel' as *;

.admin-content__loading {
    padding: $spacing-2xl;
    text-align: center;
    color: $admin-text-muted;
}
</style>
